import { useContext, useEffect, useState, type FunctionComponent } from 'react';
import { track } from '@fff-web/fff-analytics';
import { Button } from '@fff-web/fff-ui-shared';
import { useUserContext } from '@fff-web/fff-utilities';
import { postSkipBox } from '../../../services/questionnaire';
import { getSwapAmountByPlanCode } from '../../../utils/getSwapAmountByPlanCode';
import { FAQ_LINK } from '../../../utils/redirectLinks';
import { DynamicTextContext } from '../../context/DynamicTextContext';
import * as styles from './SwapConfirmation.module.css';

interface SwapConfirmationProps {
  setShowConfirmation: (x: boolean) => void;
  setHasSwapped: (x: boolean) => void;
  toastOnSwapConfirmation: () => void;
}

const wrapSeasonYearInBold = (text: string): string => {
  const seasonPattern = /\b(Spring|Summer|Fall|Winter|Autumn)\s+(\d{4})\b/g;
  return text.replace(seasonPattern, '<b>$1 $2</b>');
};

const SwapConfirmation: FunctionComponent<SwapConfirmationProps> = ({
  setShowConfirmation,
  setHasSwapped,
  toastOnSwapConfirmation,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { dynamicText, swapCreditModalContent } = useContext(DynamicTextContext);
  const { userInfo } = useUserContext();

  const afterSwap = () => {
    track({
      trackName: 'Swap Completed',
      properties: {
        swap_amount: getSwapAmountByPlanCode(userInfo?.user?.planCode),
        cta_name: 'CONFIRM',
      },
    });
    setHasSwapped(true);
    setShowConfirmation(false);
    toastOnSwapConfirmation();
    setIsLoading(true);
  };

  const handleConfirm = () => {
    setIsLoading(true);
    postSkipBox().then(() => afterSwap());
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.swapConfirmation}>
      <h1 className={styles.title}>Are You Sure You’d Like To Swap for Credit?</h1>

      <ul className={styles.bulletPoints}>
        {swapCreditModalContent && swapCreditModalContent.length > 0 ? (
          swapCreditModalContent.map((content) => (
            <li key={content._key} className={styles.bullet}>
              <span dangerouslySetInnerHTML={{ __html: wrapSeasonYearInBold(content.title) }} />
            </li>
          ))
        ) : (
          <>
            <div
              className={styles.bullet}
              dangerouslySetInnerHTML={{
                __html: `• ${dynamicText?.confirmationSeasonForCreditBullet}`,
              }}
            />
            <div className={styles.bullet}>
              • Your credit will be available at the start of the billing period.
            </div>
            {dynamicText?.swapDelayedBox && (
              <div className={styles.bullet}>{`• ${dynamicText?.swapDelayedBox}`}</div>
            )}
            <div className={styles.bullet}>• Change your mind? Unswap anytime.</div>
          </>
        )}
      </ul>

      <p className={styles.terms}>
        Terms apply, for more info{' '}
        <a className={styles.faq} target="_blank" rel="noreferrer" href={FAQ_LINK}>
          visit our FAQ
        </a>
        .
      </p>
      <div className={styles.navigation}>
        <div className={styles.confirm}>
          <Button disabled={isLoading} onClick={handleConfirm}>
            CONFIRM
          </Button>
        </div>
        <div className={styles.neverMind}>
          <Button variation="black-outline" onClick={() => setShowConfirmation(false)}>
            NEVER MIND
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SwapConfirmation;
